/**
 * Sticky banner on every operator screen surfacing two things the operator
 * needs to know but won't otherwise see:
 *
 * 1. Whether their email is verified (and a one-click resend if not).
 * 2. Subscription state — days left in trial, past-due, canceled. CTAs go
 *    to Stripe Checkout / Customer Portal.
 *
 * The banner self-hides when there's nothing to say (verified + active +
 * not-near-trial-end).
 */
import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

import { self, subscriptions } from "./api";
import { Btn } from "./Btn";
import { Icon } from "./Icon";

const HIDE_KEY = "storops.trial-banner.hidden-until";

function daysLeft(iso: string | null): number | null {
  if (!iso) return null;
  const deadline = new Date(iso);
  const diff = (deadline.getTime() - Date.now()) / (1000 * 60 * 60 * 24);
  return Math.ceil(diff);
}

export function TrialBanner() {
  const [hiddenUntil, setHiddenUntil] = useState<number>(() =>
    typeof window === "undefined" ? 0 : Number(window.localStorage.getItem(HIDE_KEY) || 0),
  );

  const me = useQuery({ queryKey: ["auth", "me"], queryFn: () => self.me(), retry: false });
  const sub = useQuery({
    queryKey: ["subscriptions", "me"],
    queryFn: () => subscriptions.me(),
    retry: false,
    enabled: me.isSuccess,
  });
  const checkout = useMutation({
    mutationFn: (plan: "solo" | "small" | "multi") => subscriptions.checkout(plan, "monthly"),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
    onError: () => toast.error("Couldn't start checkout — try again"),
  });
  const portal = useMutation({
    mutationFn: () => subscriptions.portal(),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
    onError: () => toast.error("Couldn't open the billing portal"),
  });
  const resend = useMutation({
    mutationFn: () => self.resendVerify(me.data?.email ?? ""),
    onSuccess: () => toast.success("Verify link re-sent — check your inbox"),
  });

  if (Date.now() < hiddenUntil) return null;
  if (!me.data || !sub.data) return null;

  // What's the message? Highest-severity first.
  const verifyMissing = !me.data.email_verified;
  const status = sub.data.status;
  // Prefer the server-authoritative cliff + day count (services/plan_access.py)
  // so the warning matches the trial-lock 402 exactly and doesn't ride the
  // browser clock. `cliff === undefined` means an older API that didn't send
  // it — fall back to the local day-threshold calc in that case only.
  const hasServerCliff = sub.data.cliff !== undefined;
  const cliff = sub.data.cliff ?? null;
  const trialDays = sub.data.days_remaining ?? daysLeft(sub.data.trial_ends_at);
  const isTrialing = status === "trialing" && trialDays !== null;
  const isLocked = hasServerCliff
    ? cliff === "plan_ended"
    : status === "canceled" || status === "past-due" || (isTrialing && trialDays! <= 0);
  // Tighten the language + tone near the cliff so the operator doesn't
  // sleepwalk into a trial-lock 402 on their write routes.
  const trialUrgent = hasServerCliff
    ? cliff === "plan_ending_urgent"
    : isTrialing && trialDays! > 0 && trialDays! <= 3;
  const trialEnding = hasServerCliff
    ? cliff === "plan_ending_soon"
    : isTrialing && trialDays! <= 7;

  // Active + verified + not-trialing = nothing to surface.
  if (!verifyMissing && !isTrialing && status === "active") return null;

  let tone: "info" | "warn" | "alert" = "info";
  let text = "";
  let cta: React.ReactNode = null;

  if (isLocked) {
    tone = "alert";
    text =
      status === "past-due"
        ? "Your subscription payment failed. Update your card to keep using Nestplot."
        : status === "canceled"
          ? "Your subscription is canceled. Pick a plan to keep your dashboard active."
          : "Your trial has ended. Pick a plan to keep using Nestplot.";
    cta = (
      <Btn
        kind="primary"
        size="sm"
        onClick={() => checkout.mutate("solo")}
        disabled={checkout.isPending}
      >
        {checkout.isPending ? "Opening…" : "Pick a plan"}
      </Btn>
    );
  } else if (trialUrgent) {
    // alert tone — red banner, "Last X day(s)" copy, primary CTA.
    tone = "alert";
    text =
      trialDays === 1
        ? "Last day of your trial — add a card today or your dashboard locks tomorrow."
        : `Only ${trialDays} days left in your trial — add a card to keep going.`;
    cta = (
      <Btn
        kind="primary"
        size="sm"
        onClick={() => checkout.mutate("solo")}
        disabled={checkout.isPending}
      >
        Pick a plan now
      </Btn>
    );
  } else if (trialEnding) {
    tone = "warn";
    text = `Your trial ends in ${trialDays} day${trialDays === 1 ? "" : "s"}. Add a card to keep going.`;
    cta = (
      <Btn
        kind="primary"
        size="sm"
        onClick={() => checkout.mutate("solo")}
        disabled={checkout.isPending}
      >
        Pick a plan
      </Btn>
    );
  } else if (isTrialing) {
    tone = "info";
    text = `${trialDays} days left in trial — no card required yet.`;
    cta = (
      <Btn
        kind="secondary"
        size="sm"
        onClick={() => checkout.mutate("solo")}
        disabled={checkout.isPending}
      >
        Upgrade
      </Btn>
    );
  } else if (verifyMissing) {
    tone = "warn";
    text = "Verify your email to get past the first 30 days.";
    cta = (
      <Btn
        kind="secondary"
        size="sm"
        onClick={() => resend.mutate()}
        disabled={resend.isPending}
      >
        Resend
      </Btn>
    );
  } else {
    return null;
  }

  function dismiss() {
    const tomorrow = Date.now() + 24 * 60 * 60 * 1000;
    window.localStorage.setItem(HIDE_KEY, String(tomorrow));
    setHiddenUntil(tomorrow);
  }

  const bg =
    tone === "alert"
      ? "bg-alert text-white"
      : tone === "warn"
        ? "bg-accent-2-soft text-warn"
        : "bg-accent-soft text-ink";

  return (
    <div className={`flex items-center gap-3 px-5 lg:px-7 py-2 ${bg} text-[13px]`}>
      <Icon name={tone === "alert" ? "warning" : "info"} size={14} className="shrink-0" />
      <span className="flex-1 font-medium">{text}</span>
      {sub.data.status === "active" && (
        <Btn kind="ghost" size="sm" onClick={() => portal.mutate()} disabled={portal.isPending}>
          Manage billing
        </Btn>
      )}
      {cta}
      {!isLocked && (
        <button onClick={dismiss} aria-label="Dismiss" className="opacity-70 hover:opacity-100">
          <Icon name="close" size={14} />
        </button>
      )}
    </div>
  );
}
